import React, { useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaExternalLinkAlt } from "react-icons/fa";
import styles from "./ModernHome.module.css";
import { useSetNavActiveSection } from "../Layout/NavActiveSectionContext";
import useProblems from "../../../hooks/useProblems";
import useHistoricalProblems from "../../../hooks/useHistoricalProblems";
import useBrands from "../../../hooks/useBrands";
import { IncidentSection } from "../Incidents";
import PastIncidentsSection from "../PastIncidents/PastIncidentsSection";
import PastIncidentsFilterMenu from "../PastIncidents/PastIncidentsFilterMenu";
import RefreshIndicator from "../Incidents/components/RefreshIndicator";
import { PIR_LIBRARY_URL } from "../../../constants/pirLibraryConstants";
import { trackPastIncidentsPirLibraryClick } from "../../../utils/modernUITracking";

/**
 * ModernHome - System Status Dashboard
 *
 * Features:
 * - Route-driven panels: "/" = Current Status, "/past-incidents" = Past Incidents
 *   (top-level navigation now lives in the persistent ModernHeader)
 * - Row-based incident list (not card grid)
 * - Professional status page aesthetic
 * - Background auto-refresh with 60-second interval (subtle indicator, no full page reload)
 * - Preserves data during background updates
 * - US1937851: Browse resolved incidents with infinite scroll
 */
function ModernHome({ newrelic, user }) {
  const location = useLocation();
  const activeTab = location.pathname === '/past-incidents' ? 'past' : 'current';
  const navigate = useNavigate();
  const { problems, loading, refreshing, lastUpdated, error, refresh } = useProblems();

  // On / and /past-incidents the nav must use ONLY URL-based active
  // state (NavLink). Clear any stale data-driven section published by a previously
  // viewed detail page so it never overrides the URL match here.
  const setNavActiveSection = useSetNavActiveSection();
  useEffect(() => {
    setNavActiveSection(null);
  }, [setNavActiveSection]);

  // US1984532: Past Incidents filter state — date range, priority, and brand.
  // Parse initial filter state from URL search params on mount.
  // Dates are stored and parsed as UTC so the API filter key is stable
  // regardless of the page-load timezone.
  const [pastStartDate, setPastStartDate] = useState(() => {
    const val = new URLSearchParams(location.search).get('startDate');
    return val ? new Date(val + 'T00:00:00Z') : null;
  });
  const [pastEndDate, setPastEndDate] = useState(() => {
    const val = new URLSearchParams(location.search).get('endDate');
    return val ? new Date(val + 'T00:00:00Z') : null;
  });
  const [pastPriorities, setPastPriorities] = useState(() =>
    new URLSearchParams(location.search).getAll('priority')
  );
  const [pastBrands, setPastBrands] = useState(() =>
    new URLSearchParams(location.search).getAll('brand')
  );
  const [pastExecSummaryDocument, setPastExecSummaryDocument] = useState(
    () => new URLSearchParams(location.search).get('execSummaryDocument') === 'true'
  );
  const [pastDateMode, setPastDateMode] = useState(() => {
    const sp = new URLSearchParams(location.search);
    return (sp.get('startDate') || sp.get('endDate')) ? 'custom' : null;
  });

  const { brands: brandOptions, loading: brandsLoading, error: brandsError } = useBrands();

  const handlePastFilterChange = useCallback(({ startDate, endDate, mode, priorities, brands, execSummaryDocument = false }) => {
    setPastStartDate(startDate);
    setPastEndDate(endDate);
    setPastDateMode(mode ?? null);
    setPastPriorities(priorities);
    setPastBrands(brands);
    setPastExecSummaryDocument(execSummaryDocument);

    // Sync filter state to URL so the current view can be shared by copying the address bar.
    // Dates are stored as UTC YYYY-MM-DD, matching the format the API expects.
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate.toISOString().slice(0, 10));
    if (endDate) params.set('endDate', endDate.toISOString().slice(0, 10));
    priorities.forEach(p => params.append('priority', p));
    brands.forEach(b => params.append('brand', b));
    if (execSummaryDocument) params.set('execSummaryDocument', 'true');

    const qs = params.toString();
    navigate(qs ? `/past-incidents?${qs}` : '/past-incidents', { replace: true });
  }, [navigate]);

  const {
    problems: pastProblems,
    loading: pastLoading,
    loadingMore,
    error: pastError,
    hasMore,
    loadMore,
    handleRetry,
  } = useHistoricalProblems({
    enabled: activeTab === 'past',
    startDate: pastStartDate,
    endDate: pastEndDate,
    priorities: pastPriorities,
    brands: pastBrands,
    execSummaryDocument: pastExecSummaryDocument,
  });

  return (
    <div className={styles.homeContainer}>
      {/* Sticky header: section title (top-level nav now lives in ModernHeader) */}
      <div className={styles.stickyHeader}>
        <div className={styles.titleRow}>
          {activeTab === 'current' ? (
            <div className={styles.titleContent}>
              <h2
                className={`tw-text-2xl tw-font-bold ${styles.titleText}`}
                data-testid="active-incidents-heading"
              >
                Active Incidents
                {!loading && (
                  <span className={`tw-text-2xl tw-font-bold ${styles.countText}`}>
                    {' '}({problems.length})
                  </span>
                )}
              </h2>
              <RefreshIndicator
                isRefreshing={refreshing}
                lastUpdated={lastUpdated}
                showTimestamp={!refreshing && !!lastUpdated}
              />
            </div>
          ) : (
            <div className={styles.titleContent}>
              <h2
                className={`tw-text-2xl tw-font-bold ${styles.titleText}`}
                data-testid="past-incidents-heading"
              >Past Incidents</h2>
              <div className={styles.headerActions}>
                <a
                  href={PIR_LIBRARY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.pirLibraryLink}
                  aria-label="PIR Library (opens in new tab)"
                  data-testid="past-incidents-pir-library-link"
                  onClick={() => trackPastIncidentsPirLibraryClick({}, newrelic)}
                >
                  <span className={styles.pirLibraryLinkText}>PIR Library</span>
                  <FaExternalLinkAlt aria-hidden="true" />
                </a>
                <PastIncidentsFilterMenu
                  startDate={pastStartDate}
                  endDate={pastEndDate}
                  priorities={pastPriorities}
                  brands={pastBrands}
                  execSummaryDocument={pastExecSummaryDocument}
                  brandOptions={brandOptions}
                  brandsLoading={brandsLoading}
                  brandsError={brandsError}
                  dateMode={pastDateMode}
                  onChange={handlePastFilterChange}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tab Panels */}
      <div className={styles.content}>
        <div
          role="region"
          id="region-current"
          aria-label="Active Incidents"
          hidden={activeTab !== 'current'}
          data-testid="active-incidents-region"
        >
          <IncidentSection
            title="Active Incidents"
            problems={problems}
            loading={loading}
            refreshing={refreshing}
            lastUpdated={lastUpdated}
            error={error}
            onRetry={refresh}
            emptyMessage="No active incidents at this time."
            newrelic={newrelic}
            user={user}
            showTitle={false}
          />
        </div>
        <div
          role="region"
          id="region-past"
          aria-label="Past Incidents"
          hidden={activeTab !== 'past'}
          data-testid="past-incidents-region"
        >
          <PastIncidentsSection
            problems={pastProblems}
            loading={pastLoading}
            loadingMore={loadingMore}
            error={pastError}
            hasMore={hasMore}
            loadMore={loadMore}
            handleRetry={handleRetry}
            newrelic={newrelic}
            showTitle={false}
            execSummaryDocumentFilter={pastExecSummaryDocument}
          />
        </div>
      </div>
    </div>
  );
}

export default ModernHome;
